/**
 * DefectsPanel — the shared UI for the Defects flow used by all three roles.
 *
 * Role variants:
 *   - "id"        (admin / pm / designer)  → full control: raise, assign,
 *                                             convert-to-VO, dismiss, verify,
 *                                             reject contractor rework.
 *   - "customer"  → raise defects; confirm or reject rework the ID sent over.
 *   - "contractor"→ upload rework photos; submit rework to ID.
 *
 * State machine mirrors the backend exactly (see server.py DEFECT_STATUS).
 */
import React, { useEffect, useMemo, useState } from "react";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";
import { compressImage } from "../lib/imageCompression";
import { Plus, X, CheckCircle2, AlertTriangle, Camera, Upload, Sparkles, Send, ArrowRight, Users, Image as ImageIcon, Loader2 } from "lucide-react";
import { useConfirm as useConfirmHook } from "./ConfirmProvider";
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle,
} from "./ui/dialog";

const STATUS_META = {
  raised:               { label: "Waiting on ID",         cls: "bg-[#FEF3C7] text-[#92400E] border-[#FCD34D]" },
  assigned:             { label: "With contractor",       cls: "bg-[#E0E7FF] text-[#3730A3] border-[#A5B4FC]" },
  contractor_submitted: { label: "Waiting on ID review",  cls: "bg-[#FED7AA] text-[#7C2D12] border-[#F97316]" },
  sent_to_customer:     { label: "Waiting on homeowner",  cls: "bg-[#F5F3FF] text-[#5B21B6] border-[#DDD6FE]" },
  closed:               { label: "Closed",                cls: "bg-[#DCFCE7] text-[#166534] border-[#86EFAC]" },
  dismissed:            { label: "Dismissed",             cls: "bg-[#F7F4EC] text-[#7A7571] border-[#EBE5D9]" },
  converted_to_vo:      { label: "Converted to VO",       cls: "bg-[#E0F2FE] text-[#075985] border-[#7DD3FC]" },
};

const OPEN_STATUSES = new Set(["raised", "assigned", "contractor_submitted", "sent_to_customer"]);

/**
 * Which statuses need action from a given viewer? Drives badge counts.
 */
export function needsActionFrom(viewer, status) {
  if (viewer === "id") return status === "raised" || status === "contractor_submitted";
  if (viewer === "customer") return status === "sent_to_customer";
  if (viewer === "contractor") return status === "assigned";
  return false;
}

export function countActionable(defects, viewer) {
  return (defects || []).filter((d) => needsActionFrom(viewer, d.status)).length;
}

export default function DefectsPanel({ projectId, viewer, firmContractors = [], firmStaff = [], readOnly = false, onExternalChange }) {
  const [defects, setDefects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get(`/projects/${projectId}/defects`);
      setDefects(data.defects || []);
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [projectId]);

  // Called by DefectCard whenever a status change ripples into other tabs
  // (VOs, payments, tasks). Local defects list is always refreshed; the
  // parent gets a nudge to reload its own state when supplied.
  const handleChange = async () => {
    await load();
    if (typeof onExternalChange === "function") {
      try { await onExternalChange(); } catch { /* parent handles its own errors */ }
    }
  };

  const canRaise = !readOnly && (viewer === "id" || viewer === "customer");

  const openDefects = defects.filter((d) => OPEN_STATUSES.has(d.status));
  const closedDefects = defects.filter((d) => !OPEN_STATUSES.has(d.status));

  return (
    <div className="space-y-6" data-testid="defects-panel">
      {canRaise && (
        <div className="flex items-center justify-between">
          <div className="text-sm text-[#7A7571]">
            {openDefects.length === 0
              ? "No open defects."
              : `${openDefects.length} open defect${openDefects.length === 1 ? "" : "s"}.`}
          </div>
          <button
            data-testid="defects-raise-btn"
            className="btn-primary inline-flex items-center gap-2"
            onClick={() => setCreateOpen(true)}
          >
            <Plus size={16} /> Raise a defect
          </button>
        </div>
      )}

      {loading && <div className="card-soft text-[#7A7571]">Loading…</div>}

      {!loading && openDefects.length > 0 && (
        <div className="space-y-3">
          {openDefects.map((d) => (
            <DefectCard key={d.defect_id} defect={d} viewer={viewer}
              firmContractors={firmContractors} firmStaff={firmStaff} onChange={handleChange} />
          ))}
        </div>
      )}

      {!loading && closedDefects.length > 0 && (
        <div className="pt-2">
          <div className="text-xs uppercase tracking-widest font-bold text-[#7A7571] mb-3">Closed & dismissed</div>
          <div className="space-y-3">
            {closedDefects.map((d) => (
              <DefectCard key={d.defect_id} defect={d} viewer={viewer}
                firmContractors={firmContractors} firmStaff={firmStaff} onChange={handleChange} compact />
            ))}
          </div>
        </div>
      )}

      {!loading && defects.length === 0 && (
        <div className="card-soft text-center py-12" data-testid="defects-empty">
          <div className="w-14 h-14 rounded-full bg-[#F7F4EC] flex items-center justify-center mx-auto mb-3 text-[#7A7571]">
            <CheckCircle2 size={22} />
          </div>
          <div className="font-display text-lg font-bold text-[#2C2A29]">No defects reported</div>
          <p className="text-sm text-[#7A7571] mt-2 max-w-md mx-auto">
            {viewer === "customer"
              ? "If you spot something that needs fixing, raise it here and your ID firm will handle it."
              : viewer === "contractor"
                ? "Nothing to fix right now."
                : "Homeowners can raise defects here, or you can raise one on their behalf."}
          </p>
        </div>
      )}

      <CreateDefectDialog
        open={createOpen}
        onOpenChange={setCreateOpen}
        projectId={projectId}
        viewer={viewer}
        firmContractors={firmContractors}
        firmStaff={firmStaff}
        onCreated={handleChange}
      />
    </div>
  );
}

/* -------------------------- Defect Card -------------------------- */

function DefectCard({ defect: d, viewer, firmContractors, firmStaff = [], onChange, compact }) {
  const confirm = useConfirmHook();
  const meta = STATUS_META[d.status] || STATUS_META.raised;
  const [uploading, setUploading] = useState(false);
  const [busy, setBusy] = useState(false);

  // Assign / reassign dialog state (ID only).
  // "target" controls the segmented picker inside the dialog. Defect can be
  // assigned to a contractor OR a workspace team member (admin/pm/designer).
  const [assignOpen, setAssignOpen] = useState(false);
  const [assignTarget, setAssignTarget] = useState("contractor");
  const [assignId, setAssignId] = useState("");

  // Dismiss dialog (ID only).
  const [dismissOpen, setDismissOpen] = useState(false);
  const [dismissReason, setDismissReason] = useState("");

  // Convert-to-VO dialog (ID only).
  const [convertOpen, setConvertOpen] = useState(false);
  const [convertCost, setConvertCost] = useState("");
  const [convertPayBy, setConvertPayBy] = useState("");

  // Reject dialog (ID rejects contractor submission, or customer rejects rework).
  const [rejectOpen, setRejectOpen] = useState(false);
  const [rejectReason, setRejectReason] = useState("");
  const [rejectHandler, setRejectHandler] = useState(() => async () => {});

  const uploadPhoto = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      // Shrink phone photos before hitting the wire so a 4MB shot lands as
      // ~300KB. Non-image files pass through untouched.
      const shrunk = await compressImage(file);
      const fd = new FormData();
      fd.append("file", shrunk);
      await api.post(`/defects/${d.defect_id}/photos`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("Photo uploaded");
      onChange();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setUploading(false); }
  };

  const doAction = async (path, body) => {
    setBusy(true);
    try {
      await api.post(`/defects/${d.defect_id}/${path}`, body || {});
      onChange();
      return true;
    } catch (err) { toast.error(formatApiError(err)); return false; }
    finally { setBusy(false); }
  };

  const submitRework = async () => {
    if (!(d.photos || []).some((p) => p.stage === "rework")) {
      toast.error("Upload at least one rework photo first.");
      return;
    }
    const ok = await doAction("contractor-submit");
    if (ok) toast.success("Sent to ID for review");
  };

  const idVerify = async () => {
    const ok = await doAction("id-verify");
    if (ok) toast.success("Sent to homeowner for confirmation");
  };

  const customerConfirm = async () => {
    const ok = await confirm({
      title: `Confirm "${d.title}" is fixed?`,
      message: "This will close the defect. If you'd rather ask for another fix, use 'Still broken' instead.",
      confirmText: "Confirm closed",
    });
    if (!ok) return;
    if (await doAction("customer-confirm")) toast.success("Defect closed");
  };

  const openReject = (role) => {
    setRejectReason("");
    setRejectHandler(() => async () => {
      const path = role === "id" ? "id-reject" : "customer-reject";
      const ok = await doAction(path, { reason: rejectReason.trim() || null });
      if (ok) {
        setRejectOpen(false);
        toast.success(role === "id" ? "Sent back to contractor" : "Sent back to your ID firm");
      }
    });
    setRejectOpen(true);
  };

  const submitAssign = async () => {
    if (!assignId) return;
    const body = assignTarget === "staff"
      ? { assignee_user_id: assignId }
      : { contractor_id: assignId };
    const ok = await doAction("assign", body);
    if (ok) {
      setAssignOpen(false);
      setAssignId("");
      toast.success(assignTarget === "staff" ? "Team member notified" : "Contractor assigned");
    }
  };

  const submitDismiss = async () => {
    const reason = dismissReason.trim();
    if (!reason) {
      toast.error("A reason is required.");
      return;
    }
    const ok = await doAction("dismiss", { reason });
    if (ok) {
      setDismissOpen(false);
      setDismissReason("");
      toast.success("Defect dismissed");
    }
  };

  const submitConvert = async () => {
    const cost = Number(convertCost);
    if (!cost || cost <= 0) {
      toast.error("Enter the VO cost.");
      return;
    }
    const ok = await doAction("convert-to-vo", {
      cost_delta: cost,
      pay_by_date: convertPayBy || null,
    });
    if (ok) {
      setConvertOpen(false);
      toast.success("Draft VO created — head to the VOs tab to review & send.");
    }
  };

  // Photo groups.
  const raisedPhotos = (d.photos || []).filter((p) => p.stage === "raised");
  const reworkPhotos = (d.photos || []).filter((p) => p.stage === "rework");

  return (
    <div className={`card-soft ${compact ? "!py-4 opacity-90" : ""}`} data-testid={`defect-${d.defect_id}`}>
      <div className="flex items-start justify-between gap-3 mb-2">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap mb-1">
            <h3 className="font-display text-lg font-bold text-[#2C2A29]">{d.title}</h3>
            <span
              data-testid={`defect-status-${d.defect_id}`}
              className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-widest border ${meta.cls}`}
            >
              {meta.label}
            </span>
          </div>
          <div className="text-xs text-[#7A7571]">
            Raised by {d.raised_by_name} · {new Date(d.created_at).toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" })}
            {d.contractor_name && <> · Contractor: <span className="font-semibold text-[#2C2A29]">{d.contractor_name}</span></>}
            {d.assignee_name && <> · Assigned to: <span className="font-semibold text-[#2C2A29]">{d.assignee_name}</span></>}
          </div>
        </div>
      </div>

      <p className="text-sm text-[#2C2A29] mb-3 whitespace-pre-wrap">{d.description}</p>

      {/* Reason strips */}
      {d.status === "dismissed" && d.dismissed_reason && (
        <div className="rounded-2xl bg-[#F7F4EC] border border-[#EBE5D9] p-3 text-sm text-[#7A7571] mb-3">
          <span className="font-semibold text-[#2C2A29]">Dismissed by ID:</span> {d.dismissed_reason}
        </div>
      )}
      {d.status === "assigned" && d.id_reject_note && (
        <div className="rounded-2xl bg-[#FEF3C7] border border-[#FCD34D] p-3 text-sm text-[#92400E] mb-3">
          <AlertTriangle size={12} className="inline mr-1" />
          <span className="font-semibold">ID sent back:</span> {d.id_reject_note}
        </div>
      )}
      {d.status === "assigned" && d.customer_reject_note && (
        <div className="rounded-2xl bg-[#FEE2E2] border border-[#FCA5A5] p-3 text-sm text-[#991B1B] mb-3">
          <AlertTriangle size={12} className="inline mr-1" />
          <span className="font-semibold">Homeowner said still broken:</span> {d.customer_reject_note}
        </div>
      )}

      {/* Photo galleries */}
      {raisedPhotos.length > 0 && (
        <PhotoStrip label="Reported photos" photos={raisedPhotos} defectId={d.defect_id} />
      )}
      {reworkPhotos.length > 0 && (
        <PhotoStrip label="Rework photos" photos={reworkPhotos} defectId={d.defect_id} />
      )}

      {/* Contractor photo upload — only for their own assigned defect.
          Two buttons on mobile so the phone camera opens directly (via
          capture="environment") without going through the file picker. */}
      {viewer === "contractor" && d.status === "assigned" && (
        <div className="mt-3 space-y-2">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            <label
              className={`inline-flex items-center justify-center gap-2 cursor-pointer btn-secondary text-sm ${uploading ? "opacity-50 pointer-events-none" : ""}`}
              data-testid={`defect-capture-rework-${d.defect_id}`}
            >
              <Camera size={14} /> {uploading ? "Uploading…" : "Take photo"}
              <input
                type="file"
                accept="image/*"
                capture="environment"
                hidden
                onChange={(e) => { const f = e.target.files?.[0]; if (f) uploadPhoto(f); e.target.value = ""; }}
              />
            </label>
            <label
              className={`inline-flex items-center justify-center gap-2 cursor-pointer btn-secondary text-sm ${uploading ? "opacity-50 pointer-events-none" : ""}`}
              data-testid={`defect-upload-rework-${d.defect_id}`}
            >
              <ImageIcon size={14} /> {uploading ? "Uploading…" : "Upload photo"}
              <input
                type="file"
                accept="image/*"
                hidden
                onChange={(e) => { const f = e.target.files?.[0]; if (f) uploadPhoto(f); e.target.value = ""; }}
              />
            </label>
          </div>
          {uploading && (
            <p className="text-xs text-[#7A7571] italic flex items-center gap-1.5" data-testid={`defect-upload-status-${d.defect_id}`}>
              <Loader2 size={12} className="animate-spin" /> Uploading photo — please wait before submitting.
            </p>
          )}
        </div>
      )}

      {/* Action bar per viewer + status */}
      <div className="mt-4 flex flex-wrap items-center gap-2 justify-end">
        {viewer === "id" && d.status === "raised" && (
          <>
            <button data-testid={`defect-dismiss-${d.defect_id}`} className="btn-ghost text-sm" onClick={() => setDismissOpen(true)}>Dismiss</button>
            <button data-testid={`defect-convert-${d.defect_id}`} className="btn-secondary text-sm inline-flex items-center gap-1" onClick={() => setConvertOpen(true)}>
              <Sparkles size={14} /> Convert to VO
            </button>
            <button data-testid={`defect-assign-${d.defect_id}`} className="btn-primary text-sm inline-flex items-center gap-1" onClick={() => { setAssignTarget("contractor"); setAssignId(""); setAssignOpen(true); }}>
              <Users size={14} /> Assign
            </button>
          </>
        )}
        {viewer === "id" && d.status === "assigned" && (
          <button data-testid={`defect-reassign-${d.defect_id}`} className="btn-ghost text-sm" onClick={() => {
            if (d.assignee_user_id) {
              setAssignTarget("staff");
              setAssignId(d.assignee_user_id);
            } else {
              setAssignTarget("contractor");
              setAssignId(d.contractor_id || "");
            }
            setAssignOpen(true);
          }}>
            Reassign
          </button>
        )}
        {viewer === "id" && d.status === "contractor_submitted" && (
          <>
            <button data-testid={`defect-id-reject-${d.defect_id}`} className="btn-ghost text-sm text-[#9F1239]" onClick={() => openReject("id")}>Send back to contractor</button>
            <button data-testid={`defect-id-verify-${d.defect_id}`} className="btn-primary text-sm inline-flex items-center gap-1" onClick={idVerify} disabled={busy}>
              <Send size={14} /> Verify & send to homeowner
            </button>
          </>
        )}
        {viewer === "customer" && d.status === "sent_to_customer" && (
          <>
            <button data-testid={`defect-cust-reject-${d.defect_id}`} className="btn-ghost text-sm text-[#9F1239]" onClick={() => openReject("customer")}>Still broken</button>
            <button data-testid={`defect-cust-confirm-${d.defect_id}`} className="btn-primary text-sm inline-flex items-center gap-1" onClick={customerConfirm} disabled={busy}>
              <CheckCircle2 size={14} /> Confirm closed
            </button>
          </>
        )}
        {viewer === "contractor" && d.status === "assigned" && (
          <button
            data-testid={`defect-submit-rework-${d.defect_id}`}
            className="btn-primary text-sm inline-flex items-center gap-1 disabled:opacity-50 disabled:cursor-not-allowed"
            onClick={submitRework}
            disabled={busy || uploading || reworkPhotos.length === 0}
            title={
              uploading
                ? "Waiting for photo upload to finish"
                : reworkPhotos.length === 0
                  ? "Add at least one rework photo before submitting"
                  : ""
            }
          >
            <ArrowRight size={14} /> Submit rework
          </button>
        )}
      </div>

      {/* ------ Dialogs ------ */}
      <Dialog open={assignOpen} onOpenChange={setAssignOpen}>
        <DialogContent className="max-w-md" data-testid={`defect-assign-dialog-${d.defect_id}`}>
          <DialogHeader>
            <DialogTitle>Assign defect</DialogTitle>
            <DialogDescription>Pick a contractor for the rework, or a team member to follow up internally.</DialogDescription>
          </DialogHeader>
          {/* Segmented picker: contractor vs team member */}
          <div
            role="tablist"
            className="inline-flex bg-[#F7F4EC] p-1 rounded-full border border-[#EBE5D9] w-fit"
            data-testid={`defect-assign-target-${d.defect_id}`}
          >
            {[
              { key: "contractor", label: "Contractor" },
              { key: "staff", label: "Team member" },
            ].map((opt) => (
              <button
                key={opt.key}
                role="tab"
                aria-selected={assignTarget === opt.key}
                data-testid={`defect-assign-tab-${opt.key}`}
                className={
                  "text-sm px-3.5 py-1.5 rounded-full transition " +
                  (assignTarget === opt.key
                    ? "bg-white text-[#2C2A29] shadow-sm font-semibold"
                    : "text-[#7A7571]")
                }
                onClick={() => { setAssignTarget(opt.key); setAssignId(""); }}
              >
                {opt.label}
              </button>
            ))}
          </div>
          {assignTarget === "contractor" ? (
            <select className="input-soft" data-testid="defect-assign-select" value={assignId} onChange={(e) => setAssignId(e.target.value)}>
              <option value="">Pick a contractor…</option>
              {firmContractors.map((c) => (
                <option key={c.contractor_id} value={c.contractor_id}>
                  {c.name}{c.trade ? ` · ${c.trade}` : ""}
                </option>
              ))}
            </select>
          ) : (
            <select className="input-soft" data-testid="defect-assign-staff-select" value={assignId} onChange={(e) => setAssignId(e.target.value)}>
              <option value="">Pick a team member…</option>
              {firmStaff
                .filter((s) => ["admin", "pm", "designer"].includes(s.role))
                .map((s) => (
                  <option key={s.user_id} value={s.user_id}>
                    {s.name}{s.role ? ` · ${s.role.toUpperCase()}` : ""}
                  </option>
              ))}
            </select>
          )}
          {assignTarget === "staff" && firmStaff.length === 0 && (
            <div className="text-xs text-[#9F1239]">No team members loaded — try reopening the dialog.</div>
          )}
          <div className="flex justify-end gap-2 pt-2">
            <button className="btn-ghost" onClick={() => setAssignOpen(false)}>Cancel</button>
            <button className="btn-primary" data-testid="defect-assign-submit" onClick={submitAssign} disabled={!assignId || busy}>Assign</button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={dismissOpen} onOpenChange={setDismissOpen}>
        <DialogContent className="max-w-md" data-testid={`defect-dismiss-dialog-${d.defect_id}`}>
          <DialogHeader>
            <DialogTitle>Dismiss this defect?</DialogTitle>
            <DialogDescription>Give the homeowner a clear reason — they&apos;ll see it on their side.</DialogDescription>
          </DialogHeader>
          <textarea
            className="input-soft min-h-[100px]"
            placeholder="e.g. Verified onsite — matches approved specification"
            data-testid="defect-dismiss-reason"
            value={dismissReason}
            onChange={(e) => setDismissReason(e.target.value)}
          />
          <div className="flex justify-end gap-2 pt-2">
            <button className="btn-ghost" onClick={() => setDismissOpen(false)}>Cancel</button>
            <button className="btn-primary" data-testid="defect-dismiss-submit" onClick={submitDismiss} disabled={busy || !dismissReason.trim()}>Dismiss</button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={convertOpen} onOpenChange={setConvertOpen}>
        <DialogContent className="max-w-md" data-testid={`defect-convert-dialog-${d.defect_id}`}>
          <DialogHeader>
            <DialogTitle>Convert to a Variation Order</DialogTitle>
            <DialogDescription>Use this when the &quot;defect&quot; is actually a paid scope change. A draft VO is created and you can send it to the homeowner from the VOs tab.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <label className="text-xs uppercase tracking-widest font-bold text-[#7A7571]">Cost (VO delta)</label>
              <input
                type="number" step="0.01" min="0"
                className="input-soft"
                data-testid="defect-convert-cost"
                value={convertCost}
                onChange={(e) => setConvertCost(e.target.value)}
                placeholder="0.00"
              />
            </div>
            <div>
              <label className="text-xs uppercase tracking-widest font-bold text-[#7A7571]">Pay by date (optional)</label>
              <input
                type="date"
                className="input-soft"
                data-testid="defect-convert-payby"
                value={convertPayBy}
                onChange={(e) => setConvertPayBy(e.target.value)}
              />
            </div>
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <button className="btn-ghost" onClick={() => setConvertOpen(false)}>Cancel</button>
            <button className="btn-primary" data-testid="defect-convert-submit" onClick={submitConvert} disabled={busy}>Create draft VO</button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={rejectOpen} onOpenChange={setRejectOpen}>
        <DialogContent className="max-w-md" data-testid={`defect-reject-dialog-${d.defect_id}`}>
          <DialogHeader>
            <DialogTitle>Send back to contractor</DialogTitle>
            <DialogDescription>Optional — a short note helps the contractor know what to redo.</DialogDescription>
          </DialogHeader>
          <textarea
            className="input-soft min-h-[100px]"
            placeholder="Why it isn't fixed…"
            data-testid="defect-reject-reason"
            value={rejectReason}
            onChange={(e) => setRejectReason(e.target.value)}
          />
          <div className="flex justify-end gap-2 pt-2">
            <button className="btn-ghost" onClick={() => setRejectOpen(false)}>Cancel</button>
            <button className="btn-primary" data-testid="defect-reject-submit" onClick={() => rejectHandler()} disabled={busy}>Send back</button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function PhotoStrip({ label, photos, defectId }) {
  const backend = process.env.REACT_APP_BACKEND_URL;
  const token = localStorage.getItem("rh_token");
  const src = (p) => `${backend}/api/defects/${defectId}/photos/${p.photo_id}?t=${token ? "1" : "0"}`;
  return (
    <div className="mb-3">
      <div className="text-[10px] uppercase tracking-widest font-bold text-[#7A7571] mb-2">{label}</div>
      <div className="flex gap-2 overflow-x-auto hide-scrollbar">
        {photos.map((p) => (
          <a
            key={p.photo_id}
            href={src(p)}
            target="_blank"
            rel="noreferrer"
            data-testid={`defect-photo-${p.photo_id}`}
            className="w-24 h-24 rounded-2xl border border-[#EBE5D9] bg-[#F7F4EC] flex-shrink-0 overflow-hidden hover:ring-2 hover:ring-[#E07A5F] transition"
          >
            <PhotoImg photoId={p.photo_id} defectId={defectId} />
          </a>
        ))}
      </div>
    </div>
  );
}

/**
 * Fetches the protected image via axios (so the Authorization header is set)
 * and displays a blob URL. Direct <img src> won't work because the download
 * endpoint requires the Bearer token.
 */
function PhotoImg({ photoId, defectId }) {
  const [url, setUrl] = useState(null);
  useEffect(() => {
    let objUrl;
    let cancelled = false;
    (async () => {
      try {
        const res = await api.get(`/defects/${defectId}/photos/${photoId}`, { responseType: "blob" });
        if (cancelled) return;
        objUrl = URL.createObjectURL(res.data);
        setUrl(objUrl);
      } catch { /* ignore — thumbnail just stays empty */ }
    })();
    return () => { cancelled = true; if (objUrl) URL.revokeObjectURL(objUrl); };
  }, [photoId, defectId]);
  return url ? (
    <img src={url} alt="" className="w-full h-full object-cover" />
  ) : (
    <div className="w-full h-full flex items-center justify-center text-[#7A7571]">
      <Camera size={16} />
    </div>
  );
}

/* -------------------------- Create Dialog -------------------------- */

function CreateDefectDialog({ open, onOpenChange, projectId, viewer, firmContractors, firmStaff = [], onCreated }) {
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [assignTarget, setAssignTarget] = useState("none");
  const [contractorId, setContractorId] = useState("");
  const [staffId, setStaffId] = useState("");
  const [busy, setBusy] = useState(false);
  const [files, setFiles] = useState([]);

  useEffect(() => {
    if (!open) {
      setTitle(""); setDescription("");
      setAssignTarget("none"); setContractorId(""); setStaffId("");
      setFiles([]);
    }
  }, [open]);

  const canPreAssign = viewer !== "customer";

  const submit = async () => {
    if (!title.trim() || !description.trim()) {
      toast.error("Title and description are required.");
      return;
    }
    setBusy(true);
    try {
      const payload = { title: title.trim(), description: description.trim() };
      if (canPreAssign) {
        if (assignTarget === "contractor" && contractorId) payload.contractor_id = contractorId;
        else if (assignTarget === "staff" && staffId) payload.assignee_user_id = staffId;
      }
      const { data } = await api.post(`/projects/${projectId}/defects`, payload);
      const defectId = data.defect.defect_id;
      // Sequential uploads — small batch, keeps memory + UX predictable.
      // Compress phone photos before send so a 5-photo raise doesn't push
      // ~20MB across the wire.
      for (const f of files) {
        const shrunk = await compressImage(f);
        const fd = new FormData();
        fd.append("file", shrunk);
        try {
          await api.post(`/defects/${defectId}/photos`, fd, {
            headers: { "Content-Type": "multipart/form-data" },
          });
        } catch { /* single-photo failure shouldn't nuke the whole defect */ }
      }
      toast.success("Defect raised");
      onOpenChange(false);
      onCreated?.();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="defect-create-dialog">
        <DialogHeader>
          <DialogTitle>Raise a defect</DialogTitle>
          <DialogDescription>
            {viewer === "customer"
              ? "Tell us what needs fixing and upload a few photos. Your ID firm will review and get the right person on it."
              : "Log a defect. Optionally pre-assign a contractor so the loop starts immediately."}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <label className="text-xs uppercase tracking-widest font-bold text-[#7A7571]">Title</label>
            <input
              type="text"
              className="input-soft"
              placeholder="e.g. Chipped tile in kitchen"
              data-testid="defect-create-title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
            />
          </div>
          <div>
            <label className="text-xs uppercase tracking-widest font-bold text-[#7A7571]">Description</label>
            <textarea
              className="input-soft min-h-[100px]"
              placeholder="Where is it? What's wrong?"
              data-testid="defect-create-desc"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
            />
          </div>
          {canPreAssign && (
            <div className="space-y-2">
              <label className="text-xs uppercase tracking-widest font-bold text-[#7A7571]">Pre-assign (optional)</label>
              <div
                role="tablist"
                className="inline-flex bg-[#F7F4EC] p-1 rounded-full border border-[#EBE5D9]"
                data-testid="defect-create-target"
              >
                {[
                  { key: "none", label: "Review first" },
                  { key: "contractor", label: "Contractor" },
                  { key: "staff", label: "Team member" },
                ].map((opt) => (
                  <button
                    key={opt.key}
                    type="button"
                    role="tab"
                    aria-selected={assignTarget === opt.key}
                    data-testid={`defect-create-tab-${opt.key}`}
                    className={
                      "text-xs px-3 py-1.5 rounded-full transition " +
                      (assignTarget === opt.key
                        ? "bg-white text-[#2C2A29] shadow-sm font-semibold"
                        : "text-[#7A7571]")
                    }
                    onClick={() => { setAssignTarget(opt.key); setContractorId(""); setStaffId(""); }}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
              {assignTarget === "contractor" && (
                <select
                  className="input-soft"
                  data-testid="defect-create-contractor"
                  value={contractorId}
                  onChange={(e) => setContractorId(e.target.value)}
                >
                  <option value="">Pick a contractor…</option>
                  {firmContractors.map((c) => (
                    <option key={c.contractor_id} value={c.contractor_id}>
                      {c.name}{c.trade ? ` · ${c.trade}` : ""}
                    </option>
                  ))}
                </select>
              )}
              {assignTarget === "staff" && (
                <select
                  className="input-soft"
                  data-testid="defect-create-staff"
                  value={staffId}
                  onChange={(e) => setStaffId(e.target.value)}
                >
                  <option value="">Pick a team member…</option>
                  {firmStaff
                    .filter((s) => ["admin", "pm", "designer"].includes(s.role))
                    .map((s) => (
                      <option key={s.user_id} value={s.user_id}>
                        {s.name}{s.role ? ` · ${s.role.toUpperCase()}` : ""}
                      </option>
                  ))}
                </select>
              )}
            </div>
          )}
          <div>
            <label className="text-xs uppercase tracking-widest font-bold text-[#7A7571]">Photos (optional)</label>
            <label
              className="input-soft flex items-center gap-2 cursor-pointer text-sm text-[#7A7571]"
              data-testid="defect-create-photos-btn"
            >
              <Upload size={14} />
              {files.length === 0 ? "Add photos" : `${files.length} photo${files.length === 1 ? "" : "s"} selected`}
              <input
                type="file"
                accept="image/*"
                multiple
                hidden
                onChange={(e) => setFiles(Array.from(e.target.files || []))}
              />
            </label>
            {files.length > 0 && (
              <div className="mt-2 flex gap-1 flex-wrap">
                {files.map((f, i) => (
                  <span key={i} className="inline-flex items-center gap-1 text-xs bg-[#F7F4EC] text-[#7A7571] px-2 py-0.5 rounded-full">
                    {f.name}
                    <button type="button" className="text-[#9F1239]" onClick={() => setFiles(files.filter((_, j) => j !== i))}>
                      <X size={10} />
                    </button>
                  </span>
                ))}
              </div>
            )}
          </div>
        </div>
        <div className="flex justify-end gap-2 pt-3">
          <button className="btn-ghost" onClick={() => onOpenChange(false)} disabled={busy}>Cancel</button>
          <button className="btn-primary" data-testid="defect-create-submit" onClick={submit} disabled={busy}>
            {busy ? "Raising…" : "Raise defect"}
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
